/*
 *   File : sales-breakdown.js
 *   Author : https://evoqins.com
 *   Description : Top selling product sales breakdown list
 *   Integrations : NA
 *   Version : 1.0.0
*/

import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DataTableComponent } from "../../../Component/DataTable";
import { EmptyScreen } from "../../../Component/EmptyComponent";
import { Loader } from "../../../Component/Others";
import { APIService } from "../../../Service";

const ITEMS_PER_PAGE = 10;

const SORT_FIELDS = ['date', 'quantity'];

const SalesBreakdownPage = () => {
    const navigate = useNavigate();
    const { state } = useLocation();

    const [currentPage, setCurrentPage] = useState(1);
    const [loader, setLoader] = useState(true);
    const [breakdownData, setBreakdownData] = useState({ data: [], total_count: 0 });

    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);

    const isSortingRef = useRef(false);
    const skipListFetchRef = useRef(false);

    useEffect(() => {
        window.scrollTo({
            top: 0, behavior: 'smooth'
        });
    }, []);

    useEffect(() => {
        if (!state?.product_code) {
            navigate("/", { replace: true });
            return;
        }

        if (skipListFetchRef.current) {
            skipListFetchRef.current = false;
            return;
        }

        _getSalesBreakdown();
    }, [state, currentPage]);

    const _handlePageChange = (page_num) => {
        if (isSortingRef.current) {
            return;
        }

        if (page_num === currentPage) {
            return;
        }

        setCurrentPage(page_num);
        setLoader(true);

        window.scrollTo({
            top: 0, behavior: 'smooth'
        });
    };

    const _handleSort = (column, direction) => {
        const sortBy = column.sortField || column.id || null;

        if (!sortBy || !SORT_FIELDS.includes(sortBy)) {
            return;
        }

        isSortingRef.current = true;
        setSortField(sortBy);
        setSortDirection(direction);
        setLoader(true);

        if (currentPage !== 1) {
            skipListFetchRef.current = true;
        }
        setCurrentPage(1);
        _getSalesBreakdown(1, sortBy, direction);

        setTimeout(() => {
            isSortingRef.current = false;
        }, 0);
    };

    const _getSalesBreakdown = (page_num = currentPage, sort_by = sortField, sort_order = sortDirection) => {
        if (!state?.product_code) {
            setBreakdownData({ data: [], total_count: 0 });
            setLoader(false);
            return;
        }

        const effective_sort_by = sort_by && SORT_FIELDS.includes(sort_by) ? sort_by : null;
        const effective_sort_order = effective_sort_by ? sort_order : null;

        let is_post = true;
        let url = 'CA/dashboard/get-top-selling-product/sale-breakdown-list';
        let data = {
            "product_code": state.product_code,
            "page_num": page_num,
            "limit": ITEMS_PER_PAGE,
            "sort_by": effective_sort_by ? [effective_sort_by] : null,
            "order": effective_sort_order ? [effective_sort_order] : null,
        };

        APIService(is_post, url, data).then((response) => {
            if (response?.status_code === 200) {
                const list = Array.isArray(response.data?.data) ? response.data.data : [];
                setBreakdownData({
                    ...(response.data || {}),
                    data: list,
                    total_count: response.data?.total_count || 0,
                });
            } else {
                setBreakdownData({ data: [], total_count: 0 });
            }
            setLoader(false);
        }).catch(() => {
            setBreakdownData({ data: [], total_count: 0 });
            setLoader(false);
        });
    };

    const TABLE_COLUMNS = [
        {
            name: 'Sales specialist',
            selector: row => row?.sales_specialist || "-",
            width: "200px",
        },
        {
            name: 'Store',
            selector: row => row?.store || "-",
            width: "260px",
        },
        {
            name: 'Date',
            id: 'date',
            sortField: 'date',
            selector: row => row?.date || "-",
            width: "140px",
            sortable: true,
        },
        {
            name: 'Quantity',
            id: 'quantity',
            sortField: 'quantity',
            selector: row => row?.quantity ?? "-",
            width: "120px",
            sortable: true,
        },
        {
            name: 'Invoice no',
            selector: row => row?.invoice_no || "-",
            width: "180px",
        },
        // {
        //     name: 'Status',
        //     selector: row => row?.status || "-",
        //     cell: row =>
        //         <span className={`${row?.status === "Success" ? "e-text-philippine-green" : row?.status === "Pending" ? "e-text-bright-orange" : "e-text-boston-university-red"} e-alt-font-noto-sans e-font-14 e-font-weight-500`}>
        //             {row?.status || "-"}
        //         </span>,
        //     width: "120px",
        // },
    ];

    const tableRows = Array.isArray(breakdownData?.data) ? breakdownData.data : [];

    return (
        <section className='pb-5 '>
            <div className='e-bg-lavender-blush-light padding-12px-top padding-98px-bottom padding-52px-lr'>
                <div className='e-display-flex cursor-pointer w-max-content'
                    onClick={() => navigate("/product-detail", {
                        state: {
                            scope: state?.scope,
                            start_date: state?.start_date || null,
                            end_date: state?.end_date || null,
                        }
                    })} >
                    <img src={require("../../../Assets/Image/CountryAdmin/arrow-left.svg").default}
                        draggable={false}
                        className='cursor-pointer'
                        alt='arrow'
                    />
                    <h1 className='ps-1 mb-0  e-primary-font-playfair e-font-24 line-height-32px e-text-raisin-black e-font-weight-600'>
                        Sales breakdown{state?.product_code ?
                            <span className='e-font-weight-400'>{` / ${state.product_code}`}</span>
                            : ""}
                    </h1>
                </div>
            </div>
            <div className='margin-neg-66px-top padding-52px-lr'>
                <div className='e-bg-white e-border-1px-platinum-dark e-admin-box-shadow py-4 e-border-radius-16'>
                    {loader ?
                        <Loader />
                        :
                        <>
                            {tableRows.length > 0 ?
                                <div className={`e-table e-leader-board-table`}>
                                    <DataTableComponent
                                        key={`sales-breakdown-${state?.product_code}-${currentPage}-${sortField || 'none'}-${sortDirection || 'none'}`}
                                        columns={TABLE_COLUMNS}
                                        data={tableRows}
                                        pagination={true}
                                        paginationServer={true}
                                        paginationPerPage={ITEMS_PER_PAGE}
                                        paginationTotalRows={breakdownData?.total_count || 0}
                                        paginationDefaultPage={currentPage}
                                        sortServer={true}
                                        defaultSortFieldId={sortField}
                                        defaultSortAsc={sortDirection !== 'desc'}
                                        onSort={_handleSort}
                                        onChangePage={_handlePageChange}
                                        rowClick={(row) => {
                                            if (!row?.reciept_id) return;
                                            navigate("/invoices/detail", {
                                                state: {
                                                    id: row.reciept_id,
                                                }
                                            });
                                        }} />
                                </div>
                                :
                                <div className='py-5 my-5'>
                                    <EmptyScreen message="No sales breakdown found" />
                                </div>
                            }
                        </>
                    }
                </div>
            </div>
        </section>
    );
};

export default SalesBreakdownPage;
